import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Scale, DollarSign, FileText, Trash2, Plus, AlertTriangle, Send, Camera, Receipt, Building2, Users } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/formatters';
import RecoveryStatusCard from './RecoveryStatusCard';
import ClawbackCard from './ClawbackCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DefaultRecoveryTab = ({ deal, getAuthHeaders, onRefresh }) => {
  const [legalCase, setLegalCase] = useState(null);
  const [collections, setCollections] = useState([]);
  const [collectionTotals, setCollectionTotals] = useState({});
  const [legalPnl, setLegalPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCase, setSavingCase] = useState(false);
  const [addingCollection, setAddingCollection] = useState(false);
  const [writeOffDialogOpen, setWriteOffDialogOpen] = useState(false);
  const [processingWriteOff, setProcessingWriteOff] = useState(false);
  const [addCollectionOpen, setAddCollectionOpen] = useState(false);
  const [addInvoiceOpen, setAddInvoiceOpen] = useState(false);
  const [addingInvoice, setAddingInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '', description: '', payee_contact_id: '', invoice_reference: '', notes: ''
  });
  const [lawyerContacts, setLawyerContacts] = useState([]);
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const [caseForm, setCaseForm] = useState({
    law_firm_name: '', contingency_rate: '', case_status: 'Open',
    notes: '', law_firm_email: '', attorney_name: '', cc_emails: ''
  });

  const blankCollectionRow = () => ({
    date: new Date().toISOString().split('T')[0],
    gross_collected: '',
    remit_reference: '',
    notes: '',
    fee_pct: '',
  });
  const [collectionRows, setCollectionRows] = useState([blankCollectionRow()]);

  const [emailForm, setEmailForm] = useState({ law_firm_email: '', cc_emails: '' });

  // Recovery-mode toggle state
  const [switchToLawyerOpen, setSwitchToLawyerOpen] = useState(false);
  const [switchToInHouseOpen, setSwitchToInHouseOpen] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [lawyerSwitchForm, setLawyerSwitchForm] = useState({ lawyer_contact_id: '', contingency_rate: '25' });
  const [allLawyers, setAllLawyers] = useState([]);

  // Derived: 'in_house' when no active legal_case (or archived), else 'via_lawyer'.
  // Mirrors the backend's _derive_recovery_mode() — single source of truth.
  const recoveryMode = legalCase?.recovery_mode
    || (legalCase?.exists && (legalCase?.case_status || '').toLowerCase() !== 'archived' ? 'via_lawyer' : 'in_house');
  const isViaLawyer = recoveryMode === 'via_lawyer';

  const dealId = deal?.id || deal?.deal_id;

  const fetchData = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const [caseRes, collRes, pnlRes] = await Promise.all([
        axios.get(`${API}/default-recovery/${dealId}/legal-case`, { headers: getAuthHeaders() }),
        axios.get(`${API}/default-recovery/${dealId}/collections`, { headers: getAuthHeaders() }),
        axios.get(`${API}/default-recovery/${dealId}/legal-pnl`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      ]);
      setLegalCase(caseRes.data);
      setLegalPnl(pnlRes.data);
      const hasExistingCase = !!(caseRes.data.law_firm_name || caseRes.data.attorney_name || caseRes.data.law_firm_email);
      let prefill = {
        law_firm_name: caseRes.data.law_firm_name || '',
        contingency_rate: caseRes.data.contingency_rate?.toString() || '0',
        case_status: caseRes.data.case_status || 'Open',
        notes: caseRes.data.notes || '',
        law_firm_email: caseRes.data.law_firm_email || '',
        attorney_name: caseRes.data.attorney_name || '',
        cc_emails: caseRes.data.cc_emails || ''
      };
      // First-time setup: pre-fill from the default Lawyer contact (if one is set).
      // Only kicks in when every law-firm/attorney field on the case is still empty,
      // so it never overwrites a case the user has already started.
      if (!hasExistingCase) {
        try {
          const dl = await axios.get(`${API}/contacts/default-lawyer`, { headers: getAuthHeaders() });
          if (dl.data && (dl.data.law_firm_name || dl.data.attorney_name || dl.data.law_firm_email)) {
            prefill = {
              ...prefill,
              law_firm_name: dl.data.law_firm_name || prefill.law_firm_name,
              attorney_name: dl.data.attorney_name || prefill.attorney_name,
              law_firm_email: dl.data.law_firm_email || prefill.law_firm_email,
              cc_emails: prefill.cc_emails || dl.data.cc_emails || '',
              contingency_rate: (
                prefill.contingency_rate && parseFloat(prefill.contingency_rate) > 0
                  ? prefill.contingency_rate
                  : (
                      dl.data.legal_fee_pct != null ? String(dl.data.legal_fee_pct)
                      : (dl.data.default_contingency_rate != null ? String(dl.data.default_contingency_rate) : prefill.contingency_rate)
                    )
              ),
            };
          }
        } catch (_) { /* no default lawyer set — leave fields blank */ }
      }
      setCaseForm(prefill);
      setCollections(collRes.data.collections || []);
      setCollectionTotals(collRes.data.totals || {});
    } catch (err) {
      console.error('Failed to fetch default recovery data:', err);
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveCase = async () => {
    setSavingCase(true);
    // Capture the prior case_status BEFORE the save so we only prompt when the
    // user *transitions* INTO Closed/Settled (not on every save of an
    // already-closed case).
    const priorStatus = (legalCase?.case_status || 'Open');
    const newStatus = caseForm.case_status;
    const transitionedToSafe = (
      ['Closed', 'Settled'].includes(newStatus) &&
      !['Closed', 'Settled'].includes(priorStatus)
    );
    try {
      await axios.post(`${API}/default-recovery/${dealId}/legal-case`, {
        ...caseForm,
        contingency_rate: parseFloat(caseForm.contingency_rate) || 0
      }, { headers: getAuthHeaders() });
      toast.success('Legal case info saved');
      await fetchData();
      if (onRefresh) onRefresh();

      // "Closed and Safe" write-off prompt: if the user just flipped the case
      // into Closed/Settled AND the deal still carries a positive balance AND
      // the deal isn't already written off, surface the existing write-off
      // dialog so they can decide whether to write off the residual.
      const remaining = Number(deal?.balance ?? 0);
      if (transitionedToSafe && !deal?.is_written_off && remaining > 0.01) {
        setWriteOffDialogOpen(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save legal case');
    } finally {
      setSavingCase(false);
    }
  };

  const handleAddCollection = async () => {
    // Validate every row before sending. Empty trailing rows (no date AND no
    // amount) are silently dropped so the operator can leave a blank "next row"
    // visible in the modal without it counting as input.
    const cleaned = collectionRows
      .map((r, idx) => ({ ...r, _idx: idx }))
      .filter(r => r.gross_collected !== '' || r.date !== '');

    if (cleaned.length === 0) {
      toast.error('Add at least one row');
      return;
    }

    const validationErrors = [];
    cleaned.forEach((r, i) => {
      const gross = parseFloat(r.gross_collected);
      if (!gross || gross <= 0) {
        validationErrors.push(`Row ${i + 1}: enter a valid amount`);
      }
      if (!r.date) {
        validationErrors.push(`Row ${i + 1}: date is required`);
      }
    });
    if (validationErrors.length) {
      toast.error(validationErrors.slice(0, 3).join(' • '));
      return;
    }

    setAddingCollection(true);
    try {
      const collections = cleaned.map(r => {
        const gross = parseFloat(r.gross_collected);
        const body = {
          date: r.date,
          gross_collected: gross,
          remit_reference: r.remit_reference,
          notes: r.notes,
        };
        if (isViaLawyer) {
          const raw = r.fee_pct === '' || r.fee_pct == null ? caseForm.contingency_rate : r.fee_pct;
          const feePct = parseFloat(raw) || 0;
          body.lawyer_fee = feePct > 0 ? Math.round(gross * feePct) / 100 : 0;
        }
        return body;
      });

      const res = await axios.post(
        `${API}/default-recovery/${dealId}/collections/bulk`,
        { collections },
        { headers: getAuthHeaders() }
      );
      const inserted = res.data?.inserted_count ?? collections.length;
      const failed = res.data?.failed_count ?? 0;
      const totalGross = collections.reduce((s, c) => s + c.gross_collected, 0);
      if (failed > 0) {
        toast.warning(`${inserted} recorded, ${failed} failed. ${formatCurrency(totalGross)} total.`);
      } else {
        toast.success(
          isViaLawyer
            ? `${inserted} collection${inserted !== 1 ? 's' : ''} recorded (${formatCurrency(totalGross)})`
            : `${inserted} payment${inserted !== 1 ? 's' : ''} recorded (${formatCurrency(totalGross)})`
        );
      }
      setAddCollectionOpen(false);
      setCollectionRows([blankCollectionRow()]);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 422 with row_errors structure
      if (detail?.row_errors) {
        const summary = detail.row_errors
          .slice(0, 3)
          .map(e => `Row ${e.row_index + 1}: ${(e.errors || [e.detail]).join(', ')}`)
          .join(' • ');
        toast.error(summary || 'Validation failed');
      } else {
        const msg = Array.isArray(detail)
          ? detail.map(e => e.msg || JSON.stringify(e)).join('; ')
          : (detail || 'Failed to add collection');
        toast.error(msg);
      }
    } finally {
      setAddingCollection(false);
    }
  };

  const fetchLawyerContactsIfNeeded = async () => {
    if (lawyerContacts.length > 0) return;
    try {
      const r = await axios.get(`${API}/contacts?contact_type=lawyer&limit=200`, { headers: getAuthHeaders() });
      const list = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
      setLawyerContacts(list.filter(c => c.is_lawyer));
    } catch (e) {
      // Non-fatal — invoice modal still works without payee picker pre-populated.
    }
  };

  const fetchAllLawyersForSwitch = async () => {
    if (allLawyers.length > 0) return;
    try {
      const r = await axios.get(`${API}/contacts?contact_type=lawyer&limit=500`, { headers: getAuthHeaders() });
      const list = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
      setAllLawyers(list.filter(c => c.is_lawyer));
    } catch (e) {
      toast.error('Failed to load lawyer contacts');
    }
  };

  const handleSwitchToLawyer = async () => {
    if (!lawyerSwitchForm.lawyer_contact_id) {
      toast.error('Select a lawyer contact');
      return;
    }
    const rate = parseFloat(lawyerSwitchForm.contingency_rate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('Enter a valid contingency rate (0-100)');
      return;
    }
    setSwitchingMode(true);
    try {
      await axios.post(`${API}/default-recovery/${dealId}/recovery-mode`, {
        mode: 'via_lawyer',
        lawyer_contact_id: lawyerSwitchForm.lawyer_contact_id,
        contingency_rate: rate,
      }, { headers: getAuthHeaders() });
      toast.success('Switched to Via Lawyer recovery');
      setSwitchToLawyerOpen(false);
      setLawyerSwitchForm({ lawyer_contact_id: '', contingency_rate: '25' });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch mode');
    } finally {
      setSwitchingMode(false);
    }
  };

  const handleSwitchToInHouse = async () => {
    setSwitchingMode(true);
    try {
      await axios.post(`${API}/default-recovery/${dealId}/recovery-mode`, {
        mode: 'in_house',
      }, { headers: getAuthHeaders() });
      toast.success('Switched to In-House recovery — legal case archived');
      setSwitchToInHouseOpen(false);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch mode');
    } finally {
      setSwitchingMode(false);
    }
  };

  const handleAddInvoice = async () => {
    const amt = parseFloat(invoiceForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!invoiceForm.description.trim()) { toast.error('Description is required'); return; }
    setAddingInvoice(true);
    try {
      await axios.post(`${API}/default-recovery/${dealId}/invoices`, {
        date: invoiceForm.date,
        amount: amt,
        description: invoiceForm.description.trim(),
        payee_contact_id: invoiceForm.payee_contact_id || null,
        invoice_reference: invoiceForm.invoice_reference?.trim() || null,
        notes: invoiceForm.notes?.trim() || null,
      }, { headers: getAuthHeaders() });
      toast.success(`Invoice of ${formatCurrency(amt)} recorded as Other Legal Cost`);
      setAddInvoiceOpen(false);
      setInvoiceForm({ date: new Date().toISOString().split('T')[0], amount: '', description: '', payee_contact_id: '', invoice_reference: '', notes: '' });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join('; ') : (detail || 'Failed to add invoice');
      toast.error(msg);
    } finally {
      setAddingInvoice(false);
    }
  };

  const handleDeleteCollection = async (collId) => {
    try {
      await axios.delete(`${API}/default-recovery/${dealId}/collections/${collId}`, { headers: getAuthHeaders() });
      toast.success('Collection entry deleted');
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to delete collection');
    }
  };

  const handleWriteOff = async () => {
    setProcessingWriteOff(true);
    try {
      const res = await axios.post(`${API}/default-recovery/${dealId}/write-off`, {
        notes: `Write-off of remaining balance`
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setWriteOffDialogOpen(false);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to write off deal');
    } finally {
      setProcessingWriteOff(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/default-recovery/${dealId}/send-default-package`, {
        law_firm_email: emailForm.law_firm_email || undefined,
        cc_emails: emailForm.cc_emails || undefined
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setSendEmailDialogOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send email';
      // If CSV was saved but email failed, show specific guidance
      if (detail.includes('CSV package saved')) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/default-recovery/${dealId}/default-package-csv`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `deal_${deal?.deal_id || dealId}_default_package.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Failed to download CSV');
    }
  };

  const handleReverseWriteOff = async () => {
    try {
      const res = await axios.post(`${API}/default-recovery/${dealId}/reverse-write-off`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse write-off');
    }
  };

  if (loading) return <div className="p-4 text-center text-muted-foreground">Loading default recovery data...</div>;

  const snapshot = deal?.default_snapshot;
  const isWrittenOff = deal?.is_written_off;

  return (
    <div className="space-y-6" data-testid="default-recovery-tab">
      {/* Recovery Mode Segmented Control */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="recovery-mode-toggle">
          <button
            type="button"
            onClick={() => {
              if (!isViaLawyer) {
                fetchAllLawyersForSwitch();
                setSwitchToLawyerOpen(true);
              }
            }}
            disabled={isViaLawyer}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              isViaLawyer
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-900 cursor-pointer'
            }`}
            data-testid="recovery-mode-via-lawyer-btn"
          >
            <Scale className="h-3.5 w-3.5" /> Via Lawyer
          </button>
          <button
            type="button"
            onClick={() => {
              if (isViaLawyer) {
                setSwitchToInHouseOpen(true);
              }
            }}
            disabled={!isViaLawyer}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              !isViaLawyer
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-900 cursor-pointer'
            }`}
            data-testid="recovery-mode-in-house-btn"
          >
            <Building2 className="h-3.5 w-3.5" /> In-House
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          {isViaLawyer
            ? 'Collections post as Lawyer Collection + Lawyer Fee. Existing in-house payments remain as-is.'
            : 'Collections post as plain Payments. No lawyer fee deducted.'}
        </p>
      </div>

      {/* Recovery status dropdown + Proposed Settlement (09/23). Re-fetches when the
          mode flips or collections change (refreshKey). */}
      <RecoveryStatusCard
        dealId={dealId}
        getAuthHeaders={getAuthHeaders}
        refreshKey={`${recoveryMode}|${collectionTotals.gross_collected}|${collections.length}`}
        onChanged={() => { if (onRefresh) onRefresh(); }}
      />

      {/* Commission clawback (09/29): claw back later, mark recovered / waived. */}
      <ClawbackCard deal={deal} getAuthHeaders={getAuthHeaders} onChanged={() => { if (onRefresh) onRefresh(); }} />

      {/* Default Snapshot */}
      {snapshot && (
        <Card data-testid="default-snapshot-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-4 w-4" /> Default Snapshot
              <Badge variant="destructive" className="ml-2">Defaulted {formatDate(snapshot.date)}</Badge>
              {isWrittenOff && <Badge variant="secondary" className="ml-1">Written Off</Badge>}
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1">
              Frozen point-in-time values captured the moment this deal was marked as defaulted.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Original Payback</p>
                <p className="font-semibold" data-testid="snapshot-original-payback">{formatCurrency(snapshot.original_payback)}</p>
              </div>
              {snapshot.total_discounts > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs">Effective Payback <span className="text-[10px]">(post-discount)</span></p>
                  <p className="font-semibold" data-testid="snapshot-effective-payback">{formatCurrency(snapshot.effective_payback)}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground text-xs">Net Collections</p>
                <p className="font-semibold text-emerald-700" data-testid="snapshot-net-collections">{formatCurrency(snapshot.net_collections ?? snapshot.payments_made ?? 0)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Payments (Net of Bounces)</p>
                <p className="font-semibold" data-testid="snapshot-payments-made">{formatCurrency(snapshot.payments_made)}</p>
              </div>
              {snapshot.total_refunds > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs">Total Refunds</p>
                  <p className="font-semibold text-orange-600" data-testid="snapshot-total-refunds">{formatCurrency(snapshot.total_refunds)}</p>
                </div>
              )}
              {snapshot.total_fees > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs">Existing Fees <span className="text-[10px]">(pre-default)</span></p>
                  <p className="font-semibold" data-testid="snapshot-total-fees">{formatCurrency(snapshot.total_fees)}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground text-xs">Balance at Default</p>
                <p className="font-semibold text-red-600" data-testid="snapshot-balance">{formatCurrency(snapshot.balance_at_default)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Default Fee Applied</p>
                <p className="font-semibold text-red-600" data-testid="snapshot-default-fee">{formatCurrency(snapshot.default_fee_applied)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`grid grid-cols-1 ${isViaLawyer ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Case Info — only in Via Lawyer mode */}
        {isViaLawyer && (
        <Card data-testid="legal-case-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Scale className="h-4 w-4" /> Legal Case Info
              </CardTitle>
              <Badge variant={caseForm.case_status === 'Open' ? 'default' : caseForm.case_status === 'Closed' ? 'secondary' : 'outline'}>
                {caseForm.case_status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Law Firm Name</Label>
                <Input data-testid="law-firm-name-input" value={caseForm.law_firm_name} onChange={e => setCaseForm(p => ({...p, law_firm_name: e.target.value}))} placeholder="Law firm name" />
              </div>
              <div>
                <Label className="text-xs">Attorney Name</Label>
                <Input data-testid="attorney-name-input" value={caseForm.attorney_name} onChange={e => setCaseForm(p => ({...p, attorney_name: e.target.value}))} placeholder="Attorney name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Contingency Rate (%)</Label>
                <Input data-testid="contingency-rate-input" type="number" min="0" max="100" step="0.5" value={caseForm.contingency_rate} onChange={e => setCaseForm(p => ({...p, contingency_rate: e.target.value}))} placeholder="e.g. 33" />
              </div>
              <div>
                <Label className="text-xs">Case Status</Label>
                <Select value={caseForm.case_status} onValueChange={v => setCaseForm(p => ({...p, case_status: v}))}>
                  <SelectTrigger data-testid="case-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Settled">Settled</SelectItem>
                    <SelectItem value="Closed">Closed and Safe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Law Firm Email</Label>
              <Input data-testid="law-firm-email-input" type="email" value={caseForm.law_firm_email} onChange={e => setCaseForm(p => ({...p, law_firm_email: e.target.value}))} placeholder="lawfirm@example.com" />
            </div>
            <div>
              <Label className="text-xs">CC Emails (default package)</Label>
              <Input
                data-testid="case-cc-emails-input"
                type="text"
                value={caseForm.cc_emails}
                onChange={e => setCaseForm(p => ({...p, cc_emails: e.target.value}))}
                placeholder="paralegal@firm.com, accounting@firm.com"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Comma- or semicolon-separated. These addresses get CC'd whenever the default package is emailed.</p>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea data-testid="case-notes-input" value={caseForm.notes} onChange={e => setCaseForm(p => ({...p, notes: e.target.value}))} placeholder="Case notes..." rows={2} />
            </div>
            <Button data-testid="save-case-btn" onClick={handleSaveCase} disabled={savingCase} className="w-full" size="sm">
              {savingCase ? 'Saving...' : 'Save Case Info'}
            </Button>
          </CardContent>
        </Card>
        )}

        {/* Actions Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Collection Totals */}
            <div className="grid grid-cols-3 gap-3 p-3 bg-muted/50 rounded-lg text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Gross Recovered</p>
                <p className="font-semibold text-green-600" data-testid="total-gross-recovered">{formatCurrency(collectionTotals.gross_collected)}</p>
                {(collectionTotals.lawyer_collected > 0 || collectionTotals.in_house_collected > 0) && (
                  <p className="text-[10px] text-muted-foreground">
                    lawyer {formatCurrency(collectionTotals.lawyer_collected)} · in-house {formatCurrency(collectionTotals.in_house_collected)}
                  </p>
                )}
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{isViaLawyer ? 'Contingency Fee' : 'Lawyer Fee'}</p>
                <p className="font-semibold text-orange-600">{formatCurrency(collectionTotals.contingency_fee)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{isViaLawyer ? 'Net to Funder' : 'Net Recovered'}</p>
                <p className="font-semibold">{formatCurrency(collectionTotals.net_to_funder)}</p>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button data-testid="add-collection-btn" onClick={() => setAddCollectionOpen(true)} size="sm" className="flex-1">
                <Plus className="h-3 w-3 mr-1" /> Add Collection
              </Button>
              {isViaLawyer && (
                <>
                  <Button data-testid="add-invoice-btn" onClick={() => { fetchLawyerContactsIfNeeded(); setAddInvoiceOpen(true); }} size="sm" variant="outline" className="flex-1">
                    <Receipt className="h-3 w-3 mr-1" /> Add Invoice
                  </Button>
                  <Button data-testid="send-email-btn" onClick={() => { setEmailForm({ law_firm_email: caseForm.law_firm_email, cc_emails: caseForm.cc_emails || '' }); setSendEmailDialogOpen(true); }} size="sm" variant="outline" className="flex-1">
                    <Send className="h-3 w-3 mr-1" /> Email Package
                  </Button>
                </>
              )}
            </div>
            {isViaLawyer && (
              <Button data-testid="download-csv-btn" onClick={handleDownloadCSV} size="sm" variant="outline" className="w-full">
                <FileText className="h-3 w-3 mr-1" /> Download CSV Package
              </Button>
            )}

            {!isWrittenOff && deal?.balance > 0 && (
              <Button data-testid="write-off-btn" onClick={() => setWriteOffDialogOpen(true)} size="sm" variant="destructive" className="w-full">
                <AlertTriangle className="h-3 w-3 mr-1" /> Write Off Remaining Balance ({formatCurrency(deal.balance)})
              </Button>
            )}
            {isWrittenOff && (
              <div className="space-y-2">
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 text-center">
                  This deal has been written off ({formatCurrency(deal.write_off_amount)})
                </div>
                <Button data-testid="reverse-write-off-btn" onClick={handleReverseWriteOff} size="sm" variant="outline" className="w-full text-xs">
                  Reverse Write-Off
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Legal P&L Card (Phase 2B Part 5) — title + tiles adapt to recovery mode */}
      {legalPnl && (
        <Card data-testid="legal-pnl-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Scale className="h-4 w-4" /> {isViaLawyer ? 'Legal P&L' : 'Recovery P&L'}
              <Badge variant="outline" className="text-[10px] ml-1">per deal</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${isViaLawyer ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="legal-pnl-gross">
                <div className="text-[11px] uppercase tracking-wide text-emerald-700">Gross Collected</div>
                <div className="text-lg font-bold text-emerald-800">{formatCurrency(legalPnl.gross_collected)}</div>
                {(legalPnl.lawyer_collected != null || legalPnl.in_house_collected != null) && (
                  <div className="text-[10px] text-emerald-700/80">
                    lawyer {formatCurrency(legalPnl.lawyer_collected || 0)} · in-house {formatCurrency(legalPnl.in_house_collected || 0)}
                  </div>
                )}
              </div>
              {isViaLawyer && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="legal-pnl-fees">
                  <div className="text-[11px] uppercase tracking-wide text-amber-700">Lawyer Fees Paid</div>
                  <div className="text-lg font-bold text-amber-800">{formatCurrency(legalPnl.lawyer_fees_paid)}</div>
                </div>
              )}
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="legal-pnl-net-recovery">
                <div className="text-[11px] uppercase tracking-wide text-blue-700">Net Recovery</div>
                <div className="text-lg font-bold text-blue-800">{formatCurrency(legalPnl.net_recovery)}</div>
              </div>
              <div className={`p-3 rounded-lg border ${legalPnl.net_pnl >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`} data-testid="legal-pnl-net">
                <div className={`text-[11px] uppercase tracking-wide ${legalPnl.net_pnl >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Net P&amp;L</div>
                <div className={`text-lg font-bold ${legalPnl.net_pnl >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>{formatCurrency(legalPnl.net_pnl)}</div>
              </div>
            </div>

            {/* Legal Costs breakdown — only relevant for via-lawyer mode */}
            {isViaLawyer && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="p-2 rounded bg-slate-50 border border-slate-200">
                <div className="text-[11px] text-slate-500">Settlement Legal Fees</div>
                <div className="font-mono">{formatCurrency(legalPnl.settlement_legal_fees)}</div>
              </div>
              <div className="p-2 rounded bg-slate-50 border border-slate-200">
                <div className="text-[11px] text-slate-500">Other Legal Costs</div>
                <div className="font-mono">{formatCurrency(legalPnl.legal_costs_other)}</div>
              </div>
              <div className="p-2 rounded bg-slate-100 border border-slate-300">
                <div className="text-[11px] text-slate-600 font-medium">Total Legal Costs</div>
                <div className="font-mono font-semibold">{formatCurrency(legalPnl.total_legal_costs)}</div>
              </div>
            </div>
            )}

            {/* Distribution */}
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">P&amp;L Distribution</div>
              <div className="rounded-lg border border-slate-200 overflow-hidden text-sm">
                <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-slate-50 text-[11px] text-slate-500 font-medium">
                  <div>Party</div>
                  <div className="text-right">Ownership %</div>
                  <div className="text-right">P&amp;L Share</div>
                </div>
                {(Number(legalPnl.pfs_ownership_pct || 0) > 0.005 || Math.abs(Number(legalPnl.pfs_share || 0)) > 0.005) && (
                <div className="grid grid-cols-3 gap-2 px-3 py-2 border-t" data-testid="legal-pnl-pfs-row">
                  <div className="font-medium">PFS <span className="text-[11px] text-slate-500 font-normal">(unsyndicated remainder)</span></div>
                  <div className="text-right font-mono">{Number(legalPnl.pfs_ownership_pct || 0).toFixed(2)}%</div>
                  <div className={`text-right font-mono ${legalPnl.pfs_share >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {formatCurrency(legalPnl.pfs_share)}
                  </div>
                </div>
                )}
                {(legalPnl.syndicators || []).map((s, i) => (
                  <div key={s.syndicator_id || i} className="grid grid-cols-3 gap-2 px-3 py-2 border-t" data-testid={`legal-pnl-syndicator-row-${s.syndicator_id || i}`}>
                    <div className="truncate">{s.syndicator_name || s.syndicator_id || `Syndicator ${i + 1}`}</div>
                    <div className="text-right font-mono">{Number(s.ownership_percentage || 0).toFixed(2)}%</div>
                    <div className={`text-right font-mono ${s.pnl_share >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {formatCurrency(s.pnl_share)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Other Legal Costs detail */}
            {legalPnl.legal_costs_breakdown && legalPnl.legal_costs_breakdown.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Legal Cost Entries</div>
                <div className="rounded-lg border border-slate-200 divide-y text-sm">
                  {legalPnl.legal_costs_breakdown.map((c, i) => (
                    <div key={c.id || i} className="px-3 py-2 grid grid-cols-3 gap-2" data-testid={`legal-pnl-cost-row-${i}`}>
                      <div className="text-xs text-slate-500">{c.transaction_date ? new Date(c.transaction_date).toLocaleDateString() : '—'}</div>
                      <div className="truncate">{c.description}</div>
                      <div className="text-right font-mono">{formatCurrency(c.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lawyer Collections Table */}
      <Card data-testid="collections-table-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4" /> Lawyer Collections ({collections.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {collections.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No lawyer collections recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Gross Collected</th>
                    <th className="py-2 pr-4">Lawyer Fee</th>
                    <th className="py-2 pr-4">Reference</th>
                    <th className="py-2 pr-4">Notes</th>
                    <th className="py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map(c => {
                    const feeAmt = c.contingency_fee || 0;
                    const feePct = c.contingency_rate || 0;
                    return (
                      <tr key={c.id} className="border-b last:border-0" data-testid={`collection-row-${c.id}`}>
                        <td className="py-2 pr-4">{formatDate(c.date)}</td>
                        <td className="py-2 pr-4 font-medium text-green-600">{formatCurrency(c.gross_collected)}</td>
                        <td className="py-2 pr-4 text-orange-500 text-xs">
                          {feeAmt > 0 ? `${formatCurrency(feeAmt)} (${feePct}%)` : '-'}
                        </td>
                        <td className="py-2 pr-4 text-muted-foreground">{c.remit_reference || '-'}</td>
                        <td className="py-2 pr-4 text-muted-foreground truncate max-w-[200px]">{c.notes || '-'}</td>
                        <td className="py-2">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteCollection(c.id)}>
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Collection Dialog — multi-row */}
      <Dialog open={addCollectionOpen} onOpenChange={(o) => { setAddCollectionOpen(o); if (!o) setCollectionRows([blankCollectionRow()]); }}>
        <DialogContent className="sm:max-w-5xl lg:max-w-6xl w-[95vw]" data-testid="add-collection-dialog">
          <DialogHeader>
            <DialogTitle>{isViaLawyer ? 'Add Lawyer Collections' : 'Add In-House Recovery Payments'}</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Enter one row per payment. Use <strong>+ Add row</strong> to enter multiple at once.
              {isViaLawyer ? ' Lawyer fee % defaults to the case contingency rate.' : ''}
            </p>
          </DialogHeader>

          <div className="space-y-2 max-h-[55vh] overflow-y-auto overflow-x-auto pr-1" data-testid="collection-rows">
            {/* Column headers */}
            <div className={`hidden md:grid ${isViaLawyer ? 'md:grid-cols-[120px_110px_70px_110px_180px_1fr_36px]' : 'md:grid-cols-[120px_140px_180px_1fr_36px]'} gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-1 min-w-[760px]`}>
              <div>Date</div>
              <div>{isViaLawyer ? 'Gross ($)' : 'Amount ($)'}</div>
              {isViaLawyer && <div>Fee %</div>}
              {isViaLawyer && <div>Net to PFS</div>}
              <div>{isViaLawyer ? 'Remit Ref' : 'Reference'}</div>
              <div>Notes</div>
              <div></div>
            </div>

            {collectionRows.map((row, idx) => {
              const gross = parseFloat(row.gross_collected) || 0;
              // `?? caseForm.contingency_rate` only falls through on null/undefined.
              // Empty-string row.fee_pct must also fall back, so we treat blank as
              // "use the case-level default contingency rate".
              const effectiveFeePctRaw = row.fee_pct === '' || row.fee_pct === undefined || row.fee_pct === null
                ? caseForm.contingency_rate
                : row.fee_pct;
              const feePct = parseFloat(effectiveFeePctRaw) || 0;
              const netToPfs = isViaLawyer && gross > 0 ? gross - (gross * feePct) / 100 : 0;
              const updateRow = (patch) => setCollectionRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
              const removeRow = () => setCollectionRows(rows => rows.length > 1 ? rows.filter((_, i) => i !== idx) : [blankCollectionRow()]);
              return (
                <div
                  key={idx}
                  className={`grid grid-cols-2 md:${isViaLawyer ? 'grid-cols-[120px_110px_70px_110px_180px_1fr_36px]' : 'grid-cols-[120px_140px_180px_1fr_36px]'} gap-2 items-start p-2 rounded border border-slate-200 bg-slate-50/40 min-w-[760px]`}
                  data-testid={`collection-row-${idx}`}
                >
                  <Input
                    type="date"
                    value={row.date}
                    onChange={e => updateRow({ date: e.target.value })}
                    className="h-9 text-sm"
                    data-testid={`collection-row-${idx}-date`}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={row.gross_collected}
                    onChange={e => updateRow({ gross_collected: e.target.value })}
                    placeholder="0.00"
                    className="h-9 text-sm"
                    data-testid={`collection-row-${idx}-amount`}
                  />
                  {isViaLawyer && (
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={row.fee_pct ?? ''}
                      onChange={e => updateRow({ fee_pct: e.target.value })}
                      placeholder={String(caseForm.contingency_rate ?? '25')}
                      className="h-9 text-sm"
                      data-testid={`collection-row-${idx}-fee-pct`}
                    />
                  )}
                  {isViaLawyer && (
                    <Input
                      type="text"
                      readOnly
                      tabIndex={-1}
                      value={netToPfs > 0 ? formatCurrency(netToPfs) : '—'}
                      className="h-9 text-sm bg-slate-100 font-semibold text-slate-700 cursor-default focus:outline-none focus:ring-0"
                      aria-label="Net to PFS (calculated)"
                      data-testid={`collection-row-${idx}-net`}
                    />
                  )}
                  <Input
                    value={row.remit_reference}
                    onChange={e => updateRow({ remit_reference: e.target.value })}
                    placeholder="Check #, wire ref"
                    className="h-9 text-sm"
                    data-testid={`collection-row-${idx}-ref`}
                  />
                  <Input
                    value={row.notes}
                    onChange={e => updateRow({ notes: e.target.value })}
                    placeholder="Notes (optional)"
                    className="h-9 text-sm"
                    data-testid={`collection-row-${idx}-notes`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={removeRow}
                    disabled={collectionRows.length === 1 && !row.gross_collected && !row.remit_reference}
                    className="h-9 w-9 text-slate-400 hover:text-red-600 self-start"
                    title="Remove row"
                    data-testid={`collection-row-${idx}-remove`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Add row + summary */}
          <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCollectionRows(rows => [...rows, blankCollectionRow()])}
              data-testid="collection-add-row-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add row
            </Button>
            {(() => {
              const validRows = collectionRows.filter(r => parseFloat(r.gross_collected) > 0);
              const totalGross = validRows.reduce((s, r) => s + (parseFloat(r.gross_collected) || 0), 0);
              const totalFee = isViaLawyer
                ? validRows.reduce((s, r) => {
                    const g = parseFloat(r.gross_collected) || 0;
                    const raw = r.fee_pct === '' || r.fee_pct == null ? caseForm.contingency_rate : r.fee_pct;
                    const p = parseFloat(raw) || 0;
                    return s + (g * p) / 100;
                  }, 0)
                : 0;
              const net = totalGross - totalFee;
              if (validRows.length === 0) return <span className="text-xs text-slate-400">No valid rows yet</span>;
              return (
                <div className="text-xs text-slate-600 flex gap-4" data-testid="collection-bulk-totals">
                  <span><strong>{validRows.length}</strong> row{validRows.length !== 1 ? 's' : ''}</span>
                  <span>Gross: <strong className="text-emerald-700">{formatCurrency(totalGross)}</strong></span>
                  {isViaLawyer && <span>Fees: <strong className="text-amber-700">{formatCurrency(totalFee)}</strong></span>}
                  {isViaLawyer && <span>Net: <strong>{formatCurrency(net)}</strong></span>}
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCollectionOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCollection} disabled={addingCollection} data-testid="submit-collection-btn">
              {addingCollection
                ? 'Adding...'
                : (() => {
                    const n = collectionRows.filter(r => parseFloat(r.gross_collected) > 0).length;
                    if (n <= 1) return isViaLawyer ? 'Add Collection' : 'Add Payment';
                    return isViaLawyer ? `Add ${n} Collections` : `Add ${n} Payments`;
                  })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Invoice Dialog (Phase 2D — Other Legal Costs) */}
      <Dialog open={addInvoiceOpen} onOpenChange={setAddInvoiceOpen}>
        <DialogContent data-testid="add-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Add Legal Invoice
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Filing fees, court fees, expert witnesses, etc. — flows into <strong>Other Legal Costs</strong> on the Legal P&amp;L card. Does NOT affect deal balance.
            </p>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Invoice Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={invoiceForm.date} onChange={e => setInvoiceForm(p => ({...p, date: e.target.value}))} data-testid="invoice-date-input" />
            </div>
            <div>
              <Label>Amount <span className="text-red-500">*</span></Label>
              <Input type="number" step="0.01" min="0" value={invoiceForm.amount} onChange={e => setInvoiceForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" data-testid="invoice-amount-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Description <span className="text-red-500">*</span></Label>
              <Input value={invoiceForm.description} onChange={e => setInvoiceForm(p => ({...p, description: e.target.value}))} placeholder="e.g. Filing fee — Cook County Civil" data-testid="invoice-description-input" />
            </div>
            <div>
              <Label>Payee (Lawyer)</Label>
              <Select value={invoiceForm.payee_contact_id || 'none'} onValueChange={v => setInvoiceForm(p => ({...p, payee_contact_id: v === 'none' ? '' : v}))}>
                <SelectTrigger data-testid="invoice-payee-select"><SelectValue placeholder="Select payee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {lawyerContacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice / Reference #</Label>
              <Input value={invoiceForm.invoice_reference} onChange={e => setInvoiceForm(p => ({...p, invoice_reference: e.target.value}))} placeholder="e.g. INV-2026-0142" data-testid="invoice-reference-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={invoiceForm.notes} onChange={e => setInvoiceForm(p => ({...p, notes: e.target.value}))} placeholder="Optional context" data-testid="invoice-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddInvoiceOpen(false)}>Cancel</Button>
            <Button onClick={handleAddInvoice} disabled={addingInvoice} data-testid="submit-invoice-btn">
              {addingInvoice ? 'Adding...' : 'Add Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write-Off Confirmation */}
      <AlertDialog open={writeOffDialogOpen} onOpenChange={setWriteOffDialogOpen}>
        <AlertDialogContent data-testid="write-off-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Write Off Remaining Balance?</AlertDialogTitle>
            <AlertDialogDescription>
              {(['Closed', 'Settled'].includes(caseForm.case_status))
                ? <>This case is now <strong>{caseForm.case_status === 'Closed' ? 'Closed and Safe' : 'Settled'}</strong>. The deal still has a remaining balance of <strong>{formatCurrency(deal?.balance)}</strong>.<br /><br />Would you like to write off this residual balance now? Choosing <em>Cancel</em> leaves the balance on the books — you can write it off later from this same screen.</>
                : <>This will create a Write-Off transaction of <strong>{formatCurrency(deal?.balance)}</strong> and reduce the merchant balance to zero. This action cannot be easily undone.</>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-write-off-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleWriteOff} disabled={processingWriteOff} className="bg-red-600 hover:bg-red-700" data-testid="confirm-write-off-btn">
              {processingWriteOff ? 'Processing...' : 'Confirm Write-Off'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Email Dialog */}
      <Dialog open={sendEmailDialogOpen} onOpenChange={setSendEmailDialogOpen}>
        <DialogContent data-testid="send-email-dialog">
          <DialogHeader><DialogTitle>Send Default Package</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Sends a CSV of transaction history and balances to <strong>admin@powerfundingsolutions.com</strong> and optionally to a law firm email.
          </p>
          <div>
            <Label>Law Firm Email (optional)</Label>
            <Input
              type="email"
              value={emailForm.law_firm_email}
              onChange={e => setEmailForm(p => ({ ...p, law_firm_email: e.target.value }))}
              placeholder="lawfirm@example.com"
              data-testid="email-law-firm-input"
            />
          </div>
          <div>
            <Label>CC (optional)</Label>
            <Input
              type="text"
              value={emailForm.cc_emails}
              onChange={e => setEmailForm(p => ({ ...p, cc_emails: e.target.value }))}
              placeholder="paralegal@firm.com, accounting@firm.com"
              data-testid="email-cc-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Comma- or semicolon-separated. Pre-filled from the case's saved CC list.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail} data-testid="confirm-send-email-btn">
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Switch → Via Lawyer Dialog */}
      <Dialog open={switchToLawyerOpen} onOpenChange={setSwitchToLawyerOpen}>
        <DialogContent data-testid="switch-to-lawyer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="h-4 w-4" /> Engage a Lawyer
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              From now on, new collections will post as Lawyer Collection + Lawyer Fee.
              Existing in-house payments stay as historical record.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Lawyer Contact <span className="text-red-500">*</span></Label>
              <Select
                value={lawyerSwitchForm.lawyer_contact_id || ''}
                onValueChange={v => {
                  const lawyer = allLawyers.find(l => l.id === v);
                  const rate = lawyer?.legal_fee_pct ?? lawyer?.default_contingency_rate ?? 25;
                  setLawyerSwitchForm({ lawyer_contact_id: v, contingency_rate: String(rate) });
                }}
              >
                <SelectTrigger data-testid="switch-lawyer-select">
                  <SelectValue placeholder={allLawyers.length ? 'Select a lawyer' : 'Loading lawyers...'} />
                </SelectTrigger>
                <SelectContent>
                  {allLawyers.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.company_name || l.name}{l.legal_fee_pct != null ? ` — ${l.legal_fee_pct}%` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contingency Rate (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={lawyerSwitchForm.contingency_rate}
                onChange={e => setLawyerSwitchForm(p => ({...p, contingency_rate: e.target.value}))}
                data-testid="switch-contingency-rate-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Pre-filled from the selected lawyer's <code>legal_fee_pct</code>. Defaults to 25 if unset.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwitchToLawyerOpen(false)}>Cancel</Button>
            <Button onClick={handleSwitchToLawyer} disabled={switchingMode} data-testid="confirm-switch-to-lawyer-btn">
              {switchingMode ? 'Switching...' : 'Engage Lawyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Switch → In-House Confirm */}
      <AlertDialog open={switchToInHouseOpen} onOpenChange={setSwitchToInHouseOpen}>
        <AlertDialogContent data-testid="switch-to-in-house-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Release Lawyer & Switch to In-House?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Existing legal collections on this deal will remain in the audit trail.
              The legal case will be archived (not deleted). New collections will post
              as plain in-house payments with no lawyer fee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSwitchToInHouse}
              disabled={switchingMode}
              data-testid="confirm-switch-to-in-house-btn"
            >
              {switchingMode ? 'Switching...' : 'Release & Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DefaultRecoveryTab;
